import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import { CustomerContext } from '../../contexts/CustomerContext';
import useQueryString from '../../hooks/use-query-string';
import { School, SchoolType } from '../../models/_models';
import { Student } from '../../models';
import routes from '../../routes';
import styles from '../_pages/EarlyPurchase/EarlyPurchase.module.scss';
import Typography from '../Typography';
import TextInput from '../TextInput';
import GradeInput from '../GradeInput';
import Button from '../Button';
import StudentImage from '../../models/StudentImage';
import useLocalStorage from '../../hooks/useLocalStorage';
import SchoolInput from '../SchoolInput';
import useSchools from '../../hooks/useSchools';
import { isValidEmail } from './AccessCodeOnlyForm';
import { ToastContainer, toast } from 'react-toastify';

const NOT_SUBMITTED = 'NOT_SUBMITTED'
const USED_ACCESS_CODE = 'USED_ACCESS_CODE'
const USED_STUDENT_ID = 'USED_STUDENT_ID'
const USED_STUDENT_INFO = 'USED_STUDENT_INFO'

/*
try to find by access code
- if found continue
- else (all will push to next route automatically since we don't need "authorization")
  - try to find by student id
    - if found, continue
    - else
      - try to find student by information
        - if found continue
        - else make a new student and continue
 */

const customStyles = {
  option: (provided, state) => ({
    ...provided,
    color: state.isSelected ? '#000' : '#777',
    fontFamily: 'inherit',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#777',
    fontFamily: 'inherit',
    fontSize: '0.95em',
  }),
}

export default function AnotherStudentForm() {
  const { customer, setCustomer } = useContext(CustomerContext)
  const history = useHistory()
  const { order } = useQueryString()
  const [orderStatus, setOrderStatus] = useState(NOT_SUBMITTED)

  const { currentSchool } = customer

  const [studentId, setStudentId] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [grade, setGrade] = useState('')
  const [teacher, setTeacher] = useState('')
  const [teachers, setTeachers] = useState([])
  const [isHighSchool, setIsHighSchool] = useState(false)
  const [isOrderingAhead, setIsOrderingAhead] = useLocalStorage('orderingAhead')
  const [isUsingAccessCode, setIsUsingAccessCode] = useLocalStorage('isUsingAccessCode')
  const [email, setEmail] = useState(customer.email || '');
  const [noEmail, setNoEmail] = useState(false);
  const [triedToContinueWithoutSchool, setTCWS] = useState(false);
  const schools = useSchools();

  const handleSchoolChange = (opt) => {
    const newSchool = schools.find((school) => school.id === opt.value);

    setCustomer({ ...customer, currentSchool: newSchool });
  };

  const isYearbookOrder = order === 'yearbook_order'
  const isPanoramaOrder = order === 'panorama'

  const [submitting, setSubmitting] = useState(false)

  const findStudentByInfo = () => {
    if (!email || !isValidEmail(email)) {
      setNoEmail(true);
      setSubmitting(false);
      return;
    }

    setCustomer({ ...customer, email });

    const queryParams = {
      firstName: { match: firstName },
      lastName: { match: lastName },
      schoolId: customer.currentSchool.id,
      isEnrolled: true,
      grade,
    }

    Student
      .where({ ...queryParams })
      .all()
      .then((res) => {
        // if there are too many or no results that match, then make a new student
        if (res.data.length > 1 || res.data.length === 0) {
          const newStudent = new Student({
            firstName,
            lastName,
            grade,
            teacher,
            schoolId: customer.currentSchool.id,
          })

          newStudent.save().then(() => {
            setCustomer({ ...customer, currentStudent: newStudent })
            setOrderStatus(USED_STUDENT_INFO)
          })
        } else {
          setCustomer({ ...customer, currentStudent: res.data[0] })
          setOrderStatus(USED_STUDENT_INFO)
        }
      })
      .finally(() => setTCWS(false));
  }

  useEffect(() => {
    if (!customer.currentSchool) return;

    axios.get(`/students/teachers?school_id=${customer.currentSchool.id}`)
      .then((res) => {
        setTeachers(['Staff', ...new Set(res.data.data.map((teacher) => teacher.name))]);
      })
  }, [customer?.currentSchool])

  useEffect(() => {
    if (!customer.currentSchool) return;

    if (customer.currentSchool.schoolTypeId) {
      SchoolType
        .find(customer.currentSchool.schoolTypeId)
        .then((res) => {
          setIsHighSchool(res.data.name === 'High School')
        })
    } else {
      School
        .find(customer.currentSchool.id)
        .then((res) => {
          SchoolType
            .find(res.data.schoolTypeId)
            .then((schoolTypeRes) => {
              setIsHighSchool(schoolTypeRes.data.name === 'High School')
            })
        })
    }
  }, [customer?.currentSchool])

  const queryHandler = (e) => {
    e.preventDefault()

    if (!customer.currentSchool) {
      setTCWS(true);
      return;
    }

    if (!isHighSchool && !isPanoramaOrder && teacher === '') {
      toast.error('Please select a teacher before continuing');
      return;
    }

    setSubmitting(true)

    const enrolledParam = order === 'yearbook_order' ? {} : { isEnrolled: true }

    if (studentId) {
      StudentImage
        .selectExtra(['packageId'])
        .includes('student')
        .where({ for_access_code_and_school: [studentId, currentSchool.id] })
        .all()
        .then((response) => {
          if (response.data.length === 1) {
            setCustomer({ ...customer, currentStudent: response.data[response.data.length - 1].student })
            setIsUsingAccessCode(true)
            setOrderStatus(USED_ACCESS_CODE)
            setSubmitting(false)
            return
          }

          Student
            .where({ studentId, schoolId: currentSchool.id, ...enrolledParam })
            .all()
            .then((res) => {
              if (res.data.length === 1) {
                setCustomer({ ...customer, currentStudent: res.data[res.data.length - 1] })
                setOrderStatus(USED_STUDENT_ID)
                return
              }

              findStudentByInfo()
            })
        })
    } else {
      findStudentByInfo()
    }
  }

  const studentIdBlurHandler = () => {
    const enrolledParam = order === 'yearbook_order' ? {} : { isEnrolled: true }

    if (!studentId) return
    if (!customer.currentSchool) return;

    StudentImage
      .selectExtra(['packageId'])
      .includes('student')
      .where({ for_access_code_and_school: [studentId, currentSchool.id] })
      .all()
      .then((response) => {
        if (response.data.length === 1) {
          const { student } = response.data[response.data.length - 1]
          setCustomer({ ...customer, currentStudent: response.data[response.data.length - 1].student })
          setIsUsingAccessCode(true)
          setOrderStatus(USED_ACCESS_CODE)
          setSubmitting(false)
          setFirstName(student.firstName)
          setLastName(student.lastName)
          setGrade(student.grade.toString())
          setTeacher(student.teacher)
          return
        }

        Student
          .where({ studentId, schoolId: currentSchool.id, ...enrolledParam })
          .all()
          .then((res) => {
            if (res.data.length === 1) {
              const student = res.data[res.data.length - 1]
              setCustomer({ ...customer, currentStudent: res.data[res.data.length - 1] })
              setOrderStatus(USED_STUDENT_ID)
              setFirstName(student.firstName)
              setLastName(student.lastName)
              setGrade(student.grade.toString())
              setTeacher(student.teacher)
            }
          })
      })
  }

  const continueHandler = () => {
    // setIsOrderingAhead(true)
    if (!email || !isValidEmail(email)) {
      setNoEmail(true);
      setSubmitting(false);
      return;
    }

    setCustomer({ ...customer, email });

    if (isYearbookOrder) {
      history.push(routes.yearbooks({ sid: customer.currentStudent.studentId, sch: customer.currentSchool.id }))
    } else if (isPanoramaOrder) {
      history.push(routes.panorama({ sid: customer.currentStudent.studentId, sch: customer.currentSchool.id }));
    } else {
      history.push('package_selection')
    }
  }

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      studentIdBlurHandler()
    }, 2000)

    return () => clearTimeout(delayDebounceFn)
  }, [studentId])

  useEffect(() => {
    if (!(customer && customer.currentStudent)) return
    if (orderStatus === NOT_SUBMITTED || orderStatus === USED_ACCESS_CODE) return

    // setIsOrderingAhead(true)
    if (!email || !isValidEmail(email)) {
      setNoEmail(true);
      setSubmitting(false);
      return;
    }

    setCustomer({ ...customer, email });

    if (orderStatus === USED_ACCESS_CODE) {
      localStorage.setItem('isUsingAccessCode', 'true')
    }
    // if (orderStatus === NOT_SUBMITTED ||
    //     ((orderStatus === USED_ACCESS_CODE || orderStatus === USED_STUDENT_ID) && order !== 'yearbook_order')
    // ) return

    if (isYearbookOrder) {
      history.push(routes.yearbooks({ sid: customer.currentStudent.studentId, sch: customer.currentSchool.id }))
    } else if (isPanoramaOrder) {
      history.push(routes.panorama({ sid: customer.currentStudent.studentId, sch: customer.currentSchool.id }));
    } else {
      history.push('package_selection')
    }
  }, [orderStatus, customer && customer.currentStudent])

  const onSubmit = orderStatus === 'NOT_SUBMITTED' ? queryHandler : continueHandler

  let titleText;
  if (order === 'yearbook_order') {
    titleText = 'Yearbooks';
  } else if (order === 'panorama') {
    titleText = 'Panoramas';
  }

  return (
    <>
      <ToastContainer
        position="bottom-right"
      />
      {titleText && <h1 className={styles.title}>{`Purchase ${titleText}`}</h1>}
      {noEmail && (
        <p className={styles.noStudentMsg}>
          Please enter a valid email before continuing.
        </p>
      )}
      {triedToContinueWithoutSchool && (
        <p className={styles.noStudentMsg}>
          Please select a school before continuing.
        </p>
      )}
      <form onSubmit={onSubmit} className={styles.container}>
        <label style={{ width: '100%' }}>
          Email
          <TextInput val={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <div>
          <Typography weight="bold" align="center" classes={styles.shoobBlueText}>
            Please enter your student information to continue
          </Typography>
        </div>
        <div>
          <SchoolInput onChange={handleSchoolChange} />
        </div>
        <TextInput
          fullWidth
          val={studentId}
          label="Student ID (optional)"
          onChange={(e) => setStudentId(e.target.value)}
          onBlur={studentIdBlurHandler}
        />
        <TextInput
          fullWidth
          val={firstName}
          label="First Name"
          onChange={(e) => setFirstName(e.target.value)}
        />
        <TextInput
          fullWidth
          val={lastName}
          label="Last Name"
          onChange={(e) => setLastName(e.target.value)}
        />
        <label>
          <label style={{ fontSize: '0.8em', marginLeft: '14px' }}>Grade</label>
          <GradeInput
            val={grade}
            onChange={(val) => setGrade(val)}
          />
        </label>
        {!isHighSchool && !isPanoramaOrder && (
          <>
            {teachers.length > 0 ? (
              <>
                <label style={{ fontSize: '0.8em', marginLeft: '14px', marginBottom: '-20px' }}>Teacher's Name<span style={{ color: 'red' }}>*</span></label>
                <Select
                  defaultValue={teachers[0]}
                  styles={customStyles}
                  options={teachers
                    .map((teacherName) => ({ value: teacherName, label: teacherName }))}
                  onChange={(e) => setTeacher(e.value)}
                />
              </>
            ) : (
              <TextInput
                fullWidth
                val={teacher}
                label="Teacher's Last Name"
                onChange={(e) => setTeacher(e.target.value)}
              />
            )}
          </>
        )}
        {orderStatus === USED_ACCESS_CODE && (
          <Typography align="center">
            By continuing, the user affirms that they are the legal guardian or close relative of the student whose image(s) they are requesting and that they authorize Shoob Photography to print and mail portraits of them to their home address.
          </Typography>
        )}
        <Button disabled={submitting} type="submit" fullWidth variant="submit">
          Continue
        </Button>
      </form>
    </>
  )
}
